import { getCalculationsAndMeasurements } from '@/helpers';
import { _isEmpty, _isNil } from '@/littledash';
import type { AnimalAlertV1 } from '@/model/Alert.model';
import type { Animal } from '@/model/Animal.model';
import type { Study } from '@/model/Study.model';
import * as Auth from '@/support/auth';
import { Alert, type AlertUpdateEventHandler } from '@/utils/alerts/Alert';
import { sortDescByDateStrProperty } from '@/utils/array.utils';
import type { FC } from 'react';
import { useEffect, useMemo, useState } from 'react';
import type { AlertDispatch } from './Show.utils';

interface AnimalAlertsOverviewProps {
  study: Study;
  animal: Animal;
  alerts: Array<AnimalAlertV1>;
  dispatch: AlertDispatch;
}

/**
 * Displays a summary card of unresolved animal alerts on the subject overview page,
 * allowing write users to resolve alerts inline without navigating away.
 */
export const AnimalAlertsOverview: FC<AnimalAlertsOverviewProps> = ({
  study,
  animal,
  alerts: incomingAlerts,
  dispatch,
}) => {
  const [alerts, updateAlerts] = useState(() =>
    incomingAlerts.filter((a) => _isNil(a.resolved_at)).sort(sortDescByDateStrProperty('created_at'))
  );

  useEffect(() => {
    updateAlerts(incomingAlerts.filter((a) => _isNil(a.resolved_at)).sort(sortDescByDateStrProperty('created_at')));
  }, [incomingAlerts]);

  const calculationMap = useMemo(() => getCalculationsAndMeasurements(study.settings.calculations), [study]);
  const isWriteUser = Auth.isWriteUserForStudy(study);

  const alertSettings = {
    displayHeader: false,
    displayDeferControl: false,
    displayResolveControl: isWriteUser,
    displayAlertState: false,
    displayCreatedAt: false,
  };

  const updateHandler: AlertUpdateEventHandler = async (event) => {
    if (event.type === 'resolved') {
      updateAlerts((prev) => prev.filter((a) => a.id !== event.data.id));
      dispatch({ type: 'UPDATE_ALERTS', data: [event.data] });
    }
  };

  if (_isEmpty(alerts)) {
    return null;
  }

  return (
    <div className="ui-card mb3 pa3 g3 flex flex-column">
      {alerts.map((alert) => (
        <Alert
          key={alert.id}
          alert={alert}
          calculation={calculationMap.get(alert.calculation)}
          studyApiId={study.api_id}
          animalApiId={animal.api_id}
          settings={alertSettings}
          onUpdate={updateHandler}
        />
      ))}
    </div>
  );
};
